import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, LoadingBlock } from '../components/ui.tsx';
import { WorkPage, WorkSection, StaffNavigation } from '../components/work-page.tsx';
import { api, ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';

export const staffPreferencesCopy = {
  en: {
    title: 'Staff notifications',
    staff: 'Staff',
    intro: 'These preferences belong to you and apply across your company’s locations.',
    email: 'Email me a copy of new staff announcements',
    privacy:
      'Email contains a notice and a sign-in link. Announcement text, private documents and time-off reasons stay in Staff.',
    inApp:
      'Announcements remain in Staff. When delivery is enabled, new announcements also appear in your notification center.',
    push: 'Push notifications are not available yet.',
    off: 'Staff notification delivery is not enabled for this account yet.',
    loading: 'Loading your preferences…',
    loadError: 'Your preferences could not be loaded. Refresh to try again.',
    save: 'Save preferences',
    retry: 'Retry saving',
    refresh: 'Refresh',
    saved: 'Preferences saved.',
    uncertain: 'Saving could not be confirmed. Retry the same preference before changing it.',
    rejected: 'The preference was not saved. Check your access and try again.',
    offline: 'Connect before saving. Your choice stays here while this page is open.',
  },
  es: {
    title: 'Notificaciones del personal',
    staff: 'Personal',
    intro: 'Estas preferencias son suyas y se aplican en las ubicaciones de su empresa.',
    email: 'Enviarme una copia por correo de los nuevos anuncios del personal',
    privacy:
      'El correo incluye un aviso y un enlace para iniciar sesión. El texto del anuncio, los documentos privados y los motivos de tiempo libre permanecen en Personal.',
    inApp:
      'Los anuncios permanecen en Personal. Cuando se habilita el envío, los nuevos anuncios también aparecen en su centro de notificaciones.',
    push: 'Las notificaciones push todavía no están disponibles.',
    off: 'El envío de notificaciones del personal aún no está habilitado para esta cuenta.',
    loading: 'Cargando sus preferencias…',
    loadError: 'No se pudieron cargar sus preferencias. Actualice para reintentarlo.',
    save: 'Guardar preferencias',
    retry: 'Reintentar guardado',
    refresh: 'Actualizar',
    saved: 'Preferencias guardadas.',
    uncertain: 'No se pudo confirmar el guardado. Reintente la misma preferencia antes de cambiarla.',
    rejected: 'No se guardó la preferencia. Revise su acceso y reinténtelo.',
    offline: 'Conéctese antes de guardar. Su selección permanece aquí mientras esta página esté abierta.',
  },
} as const;
type Preferences = { enabled: boolean; announcementEmail: boolean; pushAvailable: false };

export default function StaffPreferencesPage() {
  const s = useSession();
  return (
    <Content
      key={`${s.user?.companyId}:${s.user?.id}:${s.currentLocation?.id}:${s.user?.role}:${s.user?.permissions.join(',')}:${s.company?.planFeatures?.join(',')}`}
    />
  );
}
function Content() {
  const canManage = !!useSession().user?.permissions?.includes('staff_portal:manage');
  const { i18n } = useTranslation();
  const c = staffPreferencesCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const [data, setData] = useState<Preferences | null>(null);
  const [email, setEmail] = useState(false),
    [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<boolean | null>(null);
  const [error, setError] = useState(''),
    [message, setMessage] = useState('');
  const alive = useRef(true),
    generation = useRef(0),
    lock = useRef(false);
  async function load() {
    const gen = ++generation.current;
    setError('');
    try {
      const result = await api.get<Preferences>('/staff/preferences');
      if (!alive.current || gen !== generation.current) return;
      setData(result);
      setEmail(result.announcementEmail);
    } catch {
      if (!alive.current || gen !== generation.current) return;
      setData(null);
      setError(c.loadError);
    }
  }
  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
      generation.current++;
    };
  }, []);
  async function save() {
    if (lock.current || !data?.enabled) return;
    if (!navigator.onLine) {
      setError(c.offline);
      return;
    }
    lock.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    const desired = pending ?? email;
    setPending(desired);
    try {
      await api.post('/staff/preferences', { announcementEmail: desired });
      if (!alive.current) return;
      setPending(null);
      setMessage(c.saved);
    } catch (e) {
      if (!alive.current) return;
      if (e instanceof ApiRequestError && e.status >= 400 && e.status < 500) {
        setPending(null);
        setError(c.rejected);
        if ([401, 402, 403, 404].includes(e.status)) setData(null);
      } else setError(c.uncertain);
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const button = 'btn btn-secondary';
  return (
    <WorkPage title={c.title} description={c.intro}>
      <StaffNavigation active="preferences" canManage={canManage} ownDocuments={!canManage} clientNavigation />
      <WorkSection
        title={i18n.language.startsWith('es') ? 'Cómo recibe los anuncios' : 'How you receive announcements'}
      >
        {error && <Alert tone="error">{error}</Alert>}
        {message && <Alert tone="success">{message}</Alert>}
        {!data && !error && <LoadingBlock label={c.loading} />}
        {!data && error && (
          <button className={button} onClick={() => void load()}>
            {c.refresh}
          </button>
        )}
        {data && (
          <>
            {!data.enabled && (
              <p role="status" className="rounded border p-3">
                {c.off}
              </p>
            )}
            <label className="flex min-h-11 items-start gap-3 rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-brand-soft)] p-4">
              <input
                className="mt-1 h-5 w-5 shrink-0"
                type="checkbox"
                checked={email}
                disabled={!data.enabled || busy || pending !== null}
                onChange={(e) => {
                  setEmail(e.target.checked);
                  setMessage('');
                }}
              />
              <span>{c.email}</span>
            </label>
            <p className="mt-3 text-sm text-[color:var(--color-muted)]">{c.privacy}</p>
            <div className="my-5 space-y-3 border-t border-[color:var(--color-line)] pt-4">
              <p>{c.inApp}</p>
              <p className="text-sm text-[color:var(--color-muted)]">{c.push}</p>
            </div>
            <button className={button} disabled={!data.enabled || busy} onClick={() => void save()}>
              {pending !== null ? c.retry : c.save}
            </button>
          </>
        )}
      </WorkSection>
    </WorkPage>
  );
}
